import { Patient, Prescription } from '@/types';
import { useHospitalProfileStore } from '@/lib/stores/hospitalProfile';
import { buildPrescriptionPrintHtml } from '@/lib/utils/prescriptionPrintHtml';

type DoctorForPrint = {
  id: string;
  name: string;
  registrationNumber?: string;
};

function normalizePrescriptionForPrint(
  prescription: Prescription,
  patientId: string
): Prescription {
  return {
    ...prescription,
    patientId: prescription.patientId || patientId,
    medications:
      prescription.medications?.map((med) => ({
        ...med,
        frequency: med.frequency || '',
        frequencyPattern: med.frequencyPattern || '',
        frequencyDescription: med.frequencyDescription || '',
      })) ?? [],
  };
}

function openPrescriptionPrintWindow(printHtml: string): void {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) {
    console.error('Could not open print window');
    return;
  }

  printWindow.document.write(printHtml);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.print();
    printWindow.close();
  };
}

export const printPrescription = (
  patient: Patient,
  prescriptions: Prescription[],
  doctors: DoctorForPrint[] = []
) => {
  const { profile } = useHospitalProfileStore.getState();

  const latestPrescription = prescriptions
    .filter((p) => p.status === 'issued')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  if (!latestPrescription) {
    console.error('No issued prescription found');
    return;
  }

  const getDoctorName = (doctorId: string) =>
    doctors.find((d) => d.id === doctorId)?.name ?? 'Unknown Doctor';

  const getDoctorRegistration = (doctorId: string) =>
    doctors.find((d) => d.id === doctorId)?.registrationNumber ?? '';

  const printHtml = buildPrescriptionPrintHtml({
    profile,
    patientName: patient.name,
    patientAge: patient.age,
    patientGender: patient.gender,
    prescription: normalizePrescriptionForPrint(latestPrescription, patient.id),
    getDoctorName,
    getDoctorRegistration,
  });

  openPrescriptionPrintWindow(printHtml);
};
